import { useEffect, useState, type ReactElement } from 'react'
import { Lightbox } from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'

import type { ParentScreenshotRead } from '@/client'
import { formatCompactRelativeTime } from '@/lib/relative-time'

interface ParentScreenshotViewerProps {
  screenshots: ParentScreenshotRead[]
  index: number
  onClose: () => void
}

export function ParentScreenshotViewer({
  screenshots,
  index,
  onClose,
}: ParentScreenshotViewerProps): ReactElement {
  const [now, setNow] = useState(() => Date.now())
  const [current, setCurrent] = useState(index)

  useEffect(() => {
    const ticker = setInterval(() => setNow(Date.now()), 1_000)
    return () => clearInterval(ticker)
  }, [])

  const slides = screenshots.map((shot) => ({
    src: `/api/screenshots/${shot.id}/image`,
    alt: `Screenshot from ${new Date(shot.captured_at).toLocaleString()}`,
  }))
  const active = screenshots[current]

  return (
    <Lightbox
      open
      close={onClose}
      index={current}
      slides={slides}
      animation={{ swipe: 250 }}
      carousel={{
        finite: true,
        imageProps: {
          draggable: false,
          style: { WebkitTouchCallout: 'none', userSelect: 'none' },
        },
      }}
      toolbar={{ buttons: [] }}
      plugins={[Zoom]}
      on={{ view: ({ index: viewed }) => setCurrent(viewed) }}
      render={{
        controls: () => (
          <>
            <button
              type="button"
              onClick={onClose}
              className="absolute z-50 -ml-1 px-1 py-2 text-lg font-normal text-white select-none active:opacity-60"
              style={{
                left: 'max(1rem, env(safe-area-inset-left))',
                top: 'max(1rem, env(safe-area-inset-top))',
              }}
            >
              ‹ Back
            </button>
            {active && (
              <p
                aria-live="polite"
                className="pointer-events-none absolute bottom-6 left-1/2 z-50 -translate-x-1/2 text-sm font-medium text-white drop-shadow-md select-none"
                style={{ bottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
              >
                {formatCompactRelativeTime(active.captured_at, now)}
              </p>
            )}
          </>
        ),
      }}
    />
  )
}
